import { useSelector } from "react-redux";
import { PackageType } from "@b2bportal/core-packages";
import { Funnel } from "@b2bportal/core-types";
import { isFixedPackageFare } from "@/modules/packages/utilities/packageFarePrice";
import {
  PackageFlightSelectors,
  PackageSearchSelectors,
} from "@/store/packages/selectors";
import { useGetCurrentFunnel } from "@/utilities/hooks";

/**
 * Returns true when flights should display incremental (signed +/-) pricing.
 *
 * Flights is the first page of the funnel for FLIGHT_CAR packages, so
 * incremental pricing makes no sense there — there's no prior selection
 * to be "incremental" from. For other package types (HOTEL_FLIGHT,
 * HOTEL_FLIGHT_CAR), flights is page 2+ and incremental pricing is correct.
 *
 * Fixed/grid packages are the exception: the fare price is an absolute
 * fixed-points amount (see `getPackageFarePrice`), not a delta, so it must not
 * be signed — matching the hotel headline card, which shows fixed points without
 * a +/- sign.
 */
export const useIsPackageIncrementalPricing = (): boolean => {
  const currentFunnel = useGetCurrentFunnel();
  const packageType = useSelector(
    PackageSearchSelectors.getSearchFormPackageType
  );
  const packageStateByFareId = useSelector(
    PackageFlightSelectors.getPackageStateByFareId
  );

  const isFixedPricing = packageStateByFareId
    ? Object.values(packageStateByFareId).some(isFixedPackageFare)
    : false;

  return (
    currentFunnel === Funnel.Package &&
    packageType != null &&
    packageType !== PackageType.FLIGHT_CAR &&
    !isFixedPricing
  );
};
